import {
  Component,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
  ChangeDetectionStrategy,
} from '@angular/core';
import { ActionState, FlyoutOpeningData, SessionHistory } from '../flyout-models';
import { FlyoutService } from '../flyout.service';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { MatTable } from '@angular/material/table';
import { Logger, LoggerFactory } from '../../core/logger.service';
import { ActionsPropertiesDetails } from 'app/automations/automation-helpers-actions';
import IconsHelper from 'app/shared/helpers/icons-helper';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { IntegrationType } from 'app/automations/integrations-models';
import { IntegrationsService } from 'app/shared/services/integrations.service';
import { sessionVisitUrl } from 'app/data-platform/page-links';

@Component({
  selector: 'app-flyout-automation-history',
  templateUrl: './flyout-automation-history.component.html',
  styleUrls: ['./flyout-automation-history.component.scss'],
  encapsulation: ViewEncapsulation.None,
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class FlyoutAutomationHistoryComponent implements OnInit, OnDestroy {
  public customIntegrationData: { name?: string; iconUrl?: string };
  private customIntegrationSubscription: Subscription;

  constructor(
    public readonly loggerFactory: LoggerFactory,
    private readonly flyoutService: FlyoutService,
    private readonly translateService: TranslateService,
    private readonly integrationsService: IntegrationsService,
  ) {
    this.logger = loggerFactory.get('FlyoutAudienceComponent');
    this.customIntegrationSubscription = this.integrationsService.activeIntegrationsBS.subscribe((integrations) => {
      const customIntegration = integrations?.find((i) => i.type === IntegrationType.Custom);
      this.customIntegrationData = customIntegration?.data;
    });
    if (!this.integrationsService.activeIntegrationsBS.getValue()) {
      this.integrationsService.loadActiveIntegrations();
    }
  }

  ngOnDestroy(): void {
    this.customIntegrationSubscription?.unsubscribe();
  }

  private readonly logger: Logger;
  private automationId: string;
  private skip = 0;
  private take = 20;

  private ActionsPropertiesDetails = ActionsPropertiesDetails;

  columnsToDisplay = ['status-icon', 'identifier', 'date', 'status'];
  expandedElement: SessionHistory | null;
  identifier: string;
  private searchedIdentifier = '';
  dataSource: SessionHistory[];
  allActions = ActionState;
  isChecked = false;
  disabled = false;
  canShowMore = false;
  public isLoading = true;
  public isAdmin: boolean;
  @ViewChild(MatTable) table: MatTable<any>;

  @Input()
  public set flyoutOpeningData(openingData: FlyoutOpeningData) {
    this.automationId = openingData.automationId;
    this.isAdmin = openingData.isAdmin;
  }

  showAndHideException(event) {
    event.showException = !event.showException;
  }

  getHeader(sessionHistory: SessionHistory): string {
    if (sessionHistory.email && sessionHistory.email.length > 0) {
      return sessionHistory.email;
    }
    if (sessionHistory.identifier && sessionHistory.identifier.length > 0) {
      return sessionHistory.identifier;
    }
    return 'SessionId: ' + sessionHistory.sessionId;
  }

  ngOnInit(): void {
    this.dataSource = [];
    this.loadAudience(this.automationId, '');
  }

  public cancelFlyout() {
    this.flyoutService.closeAudienceFlyout(undefined);
  }

  private loadAudience(automationId: string, identifier: string) {
    const historiesPromise = this.getAutomationHistories(automationId, identifier);

    historiesPromise.then((sessionHistories: SessionHistory[]) => {
      sessionHistories.map((value) => (value.newestExecutionTime = new Date(value.newestExecutionTime)));
      sessionHistories.map((s) => {
        s.actionsDone = 0;
        s.events.forEach((e) => {
          if (e.action) {
            const actionKey = e.action + ', Triggerbee.Automations.Client';
            const action = ActionsPropertiesDetails[actionKey];
            if (action) {
              e.implementationName = action.nameAction;
              e.actionNameTranslationKey = 'automations.actions.' + action.translationKey + '.title';
            } else {
              this.logger.warn(actionKey + ' not found');
            }
          }
          e.executionTime = new Date(e.executionTime);
          e.message = this.formatMessage(e.message, e.actionState);
          switch (e.actionState) {
            case ActionState.New:
              // temporary state in BE, should never be here...
              s.unknownState = true;
              break;
            case ActionState.Waiting:
              s.waiting = true;
              break;
            case ActionState.Done:
              s.actionsDone++;
              break;
            case ActionState.Error:
              s.failed = true;
              break;
            case ActionState.Warning:
              s.warning = true;
              break;
            case ActionState.Aborted:
              s.aborted = true;
              break;
          }
        });
        if (s.actionsDone === s.totalNumberOfActions) {
          s.allDone = true;
        }
        if (s.actionsDone > s.totalNumberOfActions) {
          s.unknownState = true;
        }
      });
      if (sessionHistories.length < this.take) {
        this.canShowMore = false;
      } else {
        this.canShowMore = true;
      }
      sessionHistories = this.dataSource.concat(sessionHistories);
      this.dataSource = sessionHistories;
      this.isLoading = false;
      this.table.renderRows();
    });

    historiesPromise.catch((error) => {
      this.logger.error(error);
      this.isLoading = false;
    });
  }

  private formatMessage(message: string, actionState: string): string {
    try {
      if (message === undefined || (message === null && actionState === ActionState.Error)) {
        return this.translateService.instant('automations.history-flyout-standard-errormessage');
      }
      if (message && message.startsWith('{')) {
        const messageObj = JSON.parse(message);
        message = '';

        // Format our own properties
        if (messageObj.error) {
          message += `<b>${JSON.stringify(messageObj.error, null, 1)}</b><br/>`;
          delete messageObj.error;
        }

        if (messageObj.message) {
          message += `${messageObj.message}<br/>`;
          delete messageObj.message;
        }

        // Format all other properties as JSON
        if (Object.keys(messageObj).length > 0) {
          message += `<pre>${JSON.stringify(messageObj, null, 1)}</pre>`;
        }
        return message;
      }
    } catch (e) {
      this.logger.error(e);
    }
    return message;
  }

  skipOneAndShowMore() {
    this.skip += this.take;
    this.loadAudience(this.automationId, this.searchedIdentifier);
  }

  toggleShowOnlyError(event: MatSlideToggleChange) {
    this.isChecked = event.checked;
    this.skip = 0;
    this.dataSource = [];
    this.loadAudience(this.automationId, this.searchedIdentifier);
    this.table.renderRows();
  }

  searchIdentifier() {
    this.identifier = this.identifier?.trim() ?? '';
    if (this.identifier === this.searchedIdentifier) {
      return;
    }
    this.searchedIdentifier = this.identifier;
    this.skip = 0;
    this.dataSource = [];
    this.loadAudience(this.automationId, this.searchedIdentifier);
    this.table.renderRows();
  }

  private getAutomationHistories(automationId: string, identifier: string): Promise<SessionHistory[]> {
    if (identifier == null || identifier === undefined) {
      identifier = '';
    }
    return this.flyoutService.getAutomationActionHistory(
      automationId,
      this.isChecked,
      identifier,
      this.skip,
      this.take,
    );
  }

  gotoSessionDetails(history: SessionHistory) {
    return sessionVisitUrl(
      history.sessionId,
      (history.events ?? []).map((event) => event.executionTime),
    );
  }

  getIconPath(name: string): string {
    if (name === IntegrationType.Custom && this.customIntegrationData?.iconUrl) {
      return this.customIntegrationData.iconUrl;
    }
    return IconsHelper.getLogoPath(name);
  }

  getDisplayName(name: string): string {
    if (name === IntegrationType.Custom && this.customIntegrationData?.name) {
      return this.customIntegrationData.name;
    }
    return name;
  }
}
